import { createFocusTrap } from "focus-trap";

/**
 * Custom dropdown handler with proper focus management
 */
const navMenuFocusTrap = () => {
  console.log("Initializing dropdown with proper focus management");

  // Target the About dropdown button
  const navItemButton = document.querySelector(
    ".nav .menu-item-has-children a"
  );
  if (!navItemButton) {
    console.error("About dropdown button not found");
    return;
  }

  // Get the parent menu item
  const navItem = navItemButton.closest(".nav--about");
  if (!navItem) {
    console.error("Nav item not found");
    return;
  }

  // Get the dropdown menu
  const submenu = navItem.querySelector("ul");
  if (!submenu) {
    console.error("Submenu not found");
    return;
  }

  console.log("Found all required elements for dropdown");

  // Create focus trap for the submenu
  let focusTrap;
  try {
    focusTrap = createFocusTrap(submenu, {
      escapeDeactivates: true,
      returnFocusOnDeactivate: true,
      allowOutsideClick: true,
      fallbackFocus: navItemButton,
      onActivate: () => {
        console.log("Focus trap activated");
        navItem.setAttribute("aria-expanded", "true");
        navItemButton.setAttribute("aria-expanded", "true");
        submenu.removeAttribute("aria-hidden"); // Remove completely rather than setting to false

        // Make submenu visible
        submenu.style.display = "block";
        navItem.classList.add("is-active");
      },
      onDeactivate: () => {
        console.log("Focus trap deactivated");
        navItem.setAttribute("aria-expanded", "false");
        navItemButton.setAttribute("aria-expanded", "false");

        // Only set aria-hidden after focus moves outside
        setTimeout(() => {
          // Make sure no element inside has focus before hiding
          const hasFocusedElement = submenu.contains(document.activeElement);
          if (!hasFocusedElement) {
            submenu.setAttribute("aria-hidden", "true");
          }

          // Make submenu invisible
          submenu.style.display = "";
          navItem.classList.remove("is-active");
        }, 0);
      },
    });
    console.log("Focus trap created successfully");
  } catch (err) {
    console.error("Failed to create focus trap:", err);
    return;
  }

  // Track if dropdown is open
  let isOpen = false;

  // Handle button click
  navItemButton.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("About button clicked");

    if (isOpen) {
      // Close dropdown
      console.log("Closing dropdown");
      isOpen = false;
      try {
        focusTrap.deactivate();
      } catch (err) {
        console.error("Error deactivating focus trap:", err);
        // Manual fallback if trap fails
        navItem.setAttribute("aria-expanded", "false");
        navItemButton.setAttribute("aria-expanded", "false");
        submenu.style.display = "";
        navItem.classList.remove("is-active");
        // Only set aria-hidden after focus is elsewhere
        if (!submenu.contains(document.activeElement)) {
          submenu.setAttribute("aria-hidden", "true");
        }
      }
    } else {
      // Open dropdown
      console.log("Opening dropdown");
      isOpen = true;

      // First, make visible before activating focus trap
      submenu.style.display = "block";

      try {
        focusTrap.activate();
      } catch (err) {
        console.error("Error activating focus trap:", err);
        // Manual fallback if trap fails
        navItem.setAttribute("aria-expanded", "true");
        navItemButton.setAttribute("aria-expanded", "true");
        submenu.removeAttribute("aria-hidden");
        submenu.style.display = "block";
        navItem.classList.add("is-active");
      }
    }
  });

  // Document click to close dropdown
  document.addEventListener("click", (e) => {
    // If click is outside the nav item and dropdown is open
    if (isOpen && !navItem.contains(e.target)) {
      console.log("Clicked outside, closing dropdown");
      isOpen = false;
      try {
        focusTrap.deactivate();
      } catch (err) {
        console.error("Error deactivating focus trap on document click:", err);
      }
    }
  });

  // Keyboard navigation
  navItemButton.addEventListener("keydown", (e) => {
    if (
      (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") &&
      !isOpen
    ) {
      // Open dropdown on Enter, Space, or Arrow Down
      e.preventDefault();
      console.log("Opening dropdown via keyboard");
      isOpen = true;

      // First, make visible before activating focus trap
      submenu.style.display = "block";

      try {
        focusTrap.activate();

        // If arrow down was pressed, focus the first menu item
        if (e.key === "ArrowDown") {
          const firstLink = submenu.querySelector("a");
          if (firstLink) {
            setTimeout(() => firstLink.focus(), 10);
          }
        }
      } catch (err) {
        console.error("Error activating focus trap via keyboard:", err);
      }
    } else if (e.key === "Escape" && isOpen) {
      // Close dropdown on Escape
      e.preventDefault();
      console.log("Closing dropdown via Escape key");
      isOpen = false;
      try {
        focusTrap.deactivate();
      } catch (err) {
        console.error("Error deactivating focus trap on Escape:", err);
      }
    }
  });

  console.log("Dropdown handler setup completed");
};

// Run initialization when DOM is fully loaded
document.addEventListener("DOMContentLoaded", () => {
  console.log("DOM content loaded, initializing dropdown");
  navMenuFocusTrap();
});

export default navMenuFocusTrap;
